import { useEffect, useState } from 'react';
import { useLocation } from '@react-router';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import type { RoutesContextProps } from '@shared/core';
import { useTranslation } from '@shared/core';
import { useContactCollectorGuestTelemetry } from '../../ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from '../../contactCollectorGuest.routes';
import type { ContactInputFields, Contacts, IframeContacts, IframeContactInputFields, FieldVisibility } from '../../shared/types';
import type { OptionType } from '@withjoy/joykit';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { CountryData } from 'react-intl-tel-input';
import { isUnitedStatesCode } from '@shared/utils/address';
import { COUNTRY_LIST } from '@shared/utils/address/constants';
import { e164Regex } from '@shared/utils/validation';

export const useFormController = (
  formik: FormikProps<Contacts> | FormikProps<IframeContacts>,
  isIframeVersion: boolean,
  e164PhoneNumber: { [key: number]: string },
  setE164PhoneNumber: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>,
  coupleNames?: string,
  routeHelpers?: RoutesContextProps,
  phoneNumberRule: FieldVisibility = 'optional',
  mailingAddressRule: FieldVisibility = 'optional',
  emailRule: FieldVisibility = 'required',
  disableAddressValidation: boolean = false,
  // Caller-owned override for "advance to address validation". When supplied
  // it replaces the routeHelpers navigation, so the Form can be rendered
  // outside a routing context (e.g. inside a dialog).
  onNavigateToAddress?: () => void,
) => {
  const location = useLocation();
  const [intlTelInputEnabled] = useIsFeatureEnabled('intlTelInputEnabled');
  const { payload: contactCollectionDisablePhoneNumberValidationForFlagPayload = [] } = useFeatureValue('contactCollectionDisablePhoneNumberValidationFor');
  const { buttonInteracted } = useContactCollectorGuestTelemetry();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<{ [key: number]: boolean }>({ 0: false });
  const [phoneNumberTouched, setPhoneNumberTouched] = useState<{ [key: number]: boolean }>({ 0: false });

  const isPhoneNumberInvalid = (index: number) => {
    // If phone number is hidden, it's never invalid
    if (phoneNumberRule === 'hidden') return false;
    // Required+empty is primary-only (+1 household members are at-most-optional); format check below still applies to all.
    if (phoneNumberRule === 'required' && index === 0 && phoneNumberTouched[index] && !formik.values.contacts[index].phoneNumber) return true;
    // Otherwise check the validation
    return !!formik.values.contacts[index].phoneNumber && phoneNumberTouched[index] && !isPhoneNumberValid[index];
  };

  const isPhoneNumberInvalidWithoutFocus = (index: number) => {
    // If phone number is hidden, it's never invalid
    if (phoneNumberRule === 'hidden') return false;
    // Required+empty is primary-only (+1 household members are at-most-optional); format check below still applies to all.
    if (phoneNumberRule === 'required' && index === 0 && phoneNumberTouched[index] && !formik.values.contacts[index].phoneNumber) return true;
    // Otherwise check the validation (no focus gate — IntlTelInputWrapper doesn't emit focus/blur)
    return !!formik.values.contacts[index].phoneNumber && !isPhoneNumberValid[index];
  };

  const isEmailInvalid = (index: number) => {
    if (emailRule !== 'required') return false;
    return index === 0 && !formik.values.contacts[index].email && !!formik.touched.contacts?.[index].email;
  };

  useEffect(() => {
    if (!coupleNames && !isIframeVersion && !!routeHelpers) {
      routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.landing.path, location.search);
    }
  }, [coupleNames, isIframeVersion, routeHelpers, location.search]);

  const getFormFieldError = (fieldName: keyof ContactInputFields | IframeContactInputFields, index: number) => {
    const { errors, touched } = formik;
    const name = `contacts[${index}][${fieldName}]`;
    const error = getIn(errors, name);
    const touch = getIn(touched, name);

    return touch && error ? error : undefined;
  };

  const handleOnCountryDropdownChange = (option: OptionType | null, index: number): void => {
    if (!option) return;

    // Dropdown labels are localized for display; the backend contract carries the
    // canonical English country name, so resolve it from COUNTRY_LIST by code.
    const canonicalCountryName = COUNTRY_LIST.find(({ value }) => value === option.value)?.label ?? option.label;

    formik.setFieldValue(`contacts[${index}].country`, canonicalCountryName);
    formik.setFieldValue(`contacts[${index}].countryCode`, option.value);
  };

  const handleOnPhoneNumberChange = (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, _extension: string, index: number): void => {
    const sanitizedValue = value.replace(/[^0-9+]/g, '');
    if (!e164Regex.test(sanitizedValue)) {
      return;
    }
    setIsPhoneNumberValid(prevState => ({
      ...prevState,
      [index]: isValid || (contactCollectionDisablePhoneNumberValidationForFlagPayload as string[])?.includes(selectedCountryData?.iso2 ?? ''),
    }));
    formik.setFieldValue(`contacts[${index}].phoneNumber`, sanitizedValue);
    // remove empty spaces and - to convert to e164 format
    setE164PhoneNumber(prevState => ({ ...prevState, [index]: fullNumber.replace(/ |-/g, '').trim() }));
  };

  const handleOnPhoneNumberFocus = (isValid: boolean, value: string, selectedCountryData: CountryData, fullNumber: string, extension: string, index: number): void => {
    if (!phoneNumberTouched[index]) {
      setPhoneNumberTouched(prevState => ({ ...prevState, [index]: true }));
    }
    handleOnPhoneNumberChange(isValid, value, selectedCountryData, fullNumber, extension, index);
  };

  const handleOnSaveDetailsClick = async () => {
    // IntlTelInputWrapper already sets E.164 format directly into formik since we are using strictMode: true
    if (!intlTelInputEnabled) {
      Object.entries(e164PhoneNumber).forEach(element => element.length === 2 && formik.setFieldValue(`contacts[${element[0]}].phoneNumber`, element[1]));
    }

    // Validate form and show errors if validation fails
    const errors = await formik.validateForm();
    const hasErrors = Object.keys(errors).length > 0;

    // Check for primary contact email requirement (non-iframe version only)
    const primaryEmailMissing = emailRule === 'required' && !isIframeVersion && !formik.values.contacts[0].email;

    // Phone "required" is primary-only — +1 household members are at-most-optional, matching email.
    const primaryPhoneMissing = phoneNumberRule === 'required' && !formik.values.contacts[0].phoneNumber;

    // Check for invalid phone numbers
    const hasInvalidPhoneNumber = intlTelInputEnabled
      ? formik.values.contacts.some((_, index) => isPhoneNumberInvalidWithoutFocus(index))
      : formik.values.contacts.some((_, index) => isPhoneNumberInvalid(index));

    if (hasErrors || primaryEmailMissing || primaryPhoneMissing || hasInvalidPhoneNumber) {
      // Mark all fields as touched to show validation errors
      const touchedContacts = formik.values.contacts.map((_, index) => ({
        firstName: true,
        lastName: true,
        email: true,
        phoneNumber: true,
        address1: index === 0, // Only touch address fields for primary contact
        address2: index === 0,
        postalCode: index === 0,
        city: index === 0,
        region: index === 0,
        country: index === 0,
        countryCode: index === 0,
      }));
      formik.setTouched({ contacts: touchedContacts });

      // Mark all phone numbers as touched to show phone validation errors
      const allPhonesTouched = Object.fromEntries(formik.values.contacts.map((_, index) => [index, true]));
      setPhoneNumberTouched(allPhonesTouched);
      return;
    }

    buttonInteracted({ buttonType: 'save', element: 'saveDetails' });
    const noAddress = !formik.values.contacts[0].address1;
    const isMailingAddressHidden = mailingAddressRule === 'hidden';
    const isNationalAddress = isUnitedStatesCode(formik.values.contacts[0].countryCode);
    // Skip the address-suggestion step when there's nothing to validate: hidden, non-US,
    // or a blank address that isn't required (OPTIONAL + empty shouldn't force the detour).
    const skipAddressValidation = isMailingAddressHidden || !isNationalAddress || (noAddress && mailingAddressRule !== 'required');
    if ((isIframeVersion && (noAddress || skipAddressValidation)) || disableAddressValidation) {
      return formik.handleSubmit();
    }
    if (onNavigateToAddress && !skipAddressValidation) {
      onNavigateToAddress();
      return;
    }
    if (routeHelpers) {
      routeHelpers.goToExactRoute(skipAddressValidation ? contactCollectorGuestRoutePaths.confirmation.path : contactCollectorGuestRoutePaths.address.path, location.search);
    }
    // else { consider putting a telemetry event here for address validation skipped }
  };

  return {
    tAdmin,
    handleOnSaveDetailsClick,
    errors: {
      firstNameError: (index: number) => getFormFieldError('firstName', index),
      lastNameError: (index: number) => getFormFieldError('lastName', index),
      emailError: (index: number) => getFormFieldError('email', index),
      address1Error: (index: number) => getFormFieldError('address1', index),
      postalCodeError: (index: number) => getFormFieldError('postalCode', index),
      cityError: (index: number) => getFormFieldError('city', index),
      regionError: (index: number) => getFormFieldError('region', index),
      countryError: (index: number) => getFormFieldError('country', index),
    },
    handleOnCountryDropdownChange,
    handleOnPhoneNumberChange,
    handleOnPhoneNumberFocus,
    isPhoneNumberInvalid,
    isEmailInvalid,
    isPhoneNumberInvalidWithoutFocus,
    setIsPhoneNumberValid,
    phoneNumberTouched,
    setPhoneNumberTouched,
  };
};
